
import AppAccountReactivateEmail from "@/components/account/AccountReactivateEmail.vue";
import { AuthService } from "@/services/auth-service";
import { ToolbarService } from "@/services/toolbar-service";
import Vue from "vue";
import TinyAccountInfo from "../../components/account/TinyInfo.vue";
import { Company } from "../../models/company";
import { UserConfigService } from "@/services/user-config-service";
import { Permissions } from "@/models/user-configs/permissions";
import AppGridContainer from "@/components/grids/GridContainer.vue";
import { DashboardItem, DashboardItemsVisibleWithoutPermissions } from "@/utils/dashboard/dashboard-item";
import AppDashboardItem from "@/components/dashboard/DashboardItem.vue";
import { GridEditState } from "@/utils/grid/grid-edit-state";
import AppSettingsBar from "@/components/dashboard/SettingsBar.vue";
import AppCustomSettings from "@/components/dashboard/CustomSettings.vue";
import { DashboardSettings } from "@/models/user-configs/dashboard-settings";
import { DashboardTab } from "@/utils/dashboard/dashboard-tab";
import { TranslateResult } from "vue-i18n";
import {CashierSettings} from "@/models/user-configs/cashier-settings";
import AppQuickAddButtons from "@/components/dashboard/QuickAddButtons.vue";
import { ChartService } from "@/services";

export default Vue.extend({
	data() {
		let settings = UserConfigService.getDashboardSettings();
		return {
			loaded: false,
			UserConfigService,
			gridState: new GridEditState([], 3),
			settings,
			openedSettingsItem: null as DashboardItem | null,
			previousSettings: "",
			previousTabs: [] as any[],
			wasDrawerOpen: false,
			selectedTabIndex: settings.LastOpenedTabIndex,
			cashierSettings: null as null | CashierSettings,
			connectedDisplay: "",
			items: [] as DashboardItem[],
			tabs: [] as DashboardTab[]
		};
	},
	computed: {
		isDemo(): Boolean{
			return AuthService.wfCompany.IsDemo;
		},
		needsActivation(): Boolean{
			return !AuthService.wfUser.Activated;
		},
		wfCompany(): Company {
			return AuthService.wfCompany;
		},
		welcomMsg(): string {
			let today = new Date();
			let hourNow = today.getHours();

			if (hourNow < 12) {
				return this.$ct("welcome.morning").toString();
			} else if (hourNow < 18) {
				return this.$ct("welcome.noon").toString();
			} else if (hourNow < 24) {
				return this.$ct("welcome.evening").toString();
			}
			return this.$ct("welcome.welcome").toString();

		},
		firstName(): string {
			if (!AuthService.wfUser) {
				return "";
			}
			return AuthService.wfUser.Firstname;
		},
		hasPermission():boolean {
			return UserConfigService.hasCurrentUserPermission((permissions:Permissions):boolean=>{
				return permissions.CanSeeDashboard;
			});
		},
		selectedTab():DashboardTab | null{
			if (this.tabs.length == 0){
				console.log("No tabs");
				return null;
			}
			let index = this.selectedTabIndex;
			if (index >= this.tabs.length){
				index = this.tabs.length -1;
			}
			return this.tabs[index] || null;
		},
		editMode: {
			get():boolean{
				return this.gridState.editMode;
			},
			set(val:boolean){
				if (val == this.gridState.editMode) return;
				if (val){
					this.wasDrawerOpen = ToolbarService.getIsDrawerOpen();
					ToolbarService.drawer = false;
				}else{
					ToolbarService.drawer = this.wasDrawerOpen;
				}
				this.gridState.editMode = val;
			}
		},
		gridContainerWidth():string{
			if (this.editMode){
				return "9";
			}
			return "12";
		},
		settingsBarWidth():string{
			if (this.editMode){
				return "3";
			}
			return "0";
		},
		tabItems():string[]{
			return this.tabs.map(t=>t.Name);
		},
		showTabs():boolean{
			return this.tabs.length > 0;
		},
		dateMsg():TranslateResult{
			let date = new Date();
			let day = this.$ct(`common.weekday.${date.getDay()-1}`);
			let month = this.$ct(`common.months.${date.getMonth()+1}`);
			return this.$ct("date-msg", {day, month, date: date.getDate(), year: date.getFullYear()});
		},
		showQuickAddButtons():boolean {
			if (!this.selectedTab) return false;
			if (this.gridState.editMode) {
				return true;
			}
			if (this.selectedTab.QuickAddButtons.length > 0) {
				return true;
			}
			return false;
		},
		canEditCurrentTab():boolean {
			if (!this.selectedTab) return false;
			return this.selectedTab.UserID == AuthService.wfUser.ID;
		}
	},
	watch: {
		"$wf.isSmDown": {
			handler(){
				if (this.$wf.isSmDown){
					this.editMode = false;
				}
			}
		},
		"items": {
			immediate: true,
			handler(){
				this.gridState.items = this.items;
			}
		},
		"selectedTabIndex": {
			immediate: true,
			handler(){
				if (this.selectedTabIndex >= this.tabs.length && this.tabs.length > 0) {
					this.selectedTabIndex = this.tabs.length-1;
				}
			}
		},
		"tabs.length": {
			handler(){
				if (this.selectedTabIndex >= this.tabs.length && this.tabs.length > 0) {
					this.selectedTabIndex = this.tabs.length-1;
				}
			}
		}
	},
	methods: {
		onTabsSorted(){
			this.loadItems();
		},
		async changeTab(event:number){
			if (this.selectedTab) {
				this.items = this.selectedTab.Items;
			}
			this.selectedTabIndex = event;
			this.loadItems();
			await UserConfigService.setDashboardLastOpenedTabIndex(this.selectedTabIndex);
		},
		setEditMode(){
			if (this.$wf.isSmDown){
				this.gridState.editMode = false;
				return;
			}
			this.editMode = true;
			this.previousSettings = JSON.stringify(this.settings.getJSON());
		},
		cancelEdit(){
			if (!this.previousSettings){
				this.settings = UserConfigService.getDashboardSettings();
			}else{
				this.settings = new DashboardSettings(JSON.parse(this.previousSettings));
			}
			this.tabs = this.previousTabs.map(t=>new DashboardTab(t));
			this.loadItems();
			window.dispatchEvent(new Event("resize"));
			this.editMode = false;
		},
		async saveEdit(){
			if (this.selectedTab) {
				this.selectedTab.Items = this.items;
			}
			this.settings.LastOpenedTabIndex = this.selectedTabIndex;
			this.tabs = await ChartService.setDashboardTabs(this.tabs);
			this.previousTabs = this.tabs.map(t=>t.getJSON());
			this.settings.TabOrder = this.tabs.map(t=>t.ID);
			await UserConfigService.setDashboardSettings(this.settings);
			this.editMode = false;
			window.dispatchEvent(new Event("resize"));
			this.fetch();
		},
		fetch(){
			let els = this.$refs.dashboardItems as any[];
			for (let el of els){
				if (el.fetch) {
					el.fetch();
				}
			}
		},
		isAllowed(mod:number):boolean{
			return AuthService.hasModule(mod);
		},
		openSettings(item:DashboardItem){
			this.openedSettingsItem = item;
		},
		loadItems(){
			if (!this.selectedTab) return;
			let result = this.selectedTab.Items.map(i=>new DashboardItem(i.getJSON()));
			if (!this.hasPermission){
				result = result.filter(r=>DashboardItemsVisibleWithoutPermissions.indexOf(r.Type) != -1);
			}
			this.items = result;
		},
		async fetchTabs(){
			let tabs = await ChartService.getDashboardTabs();
			this.tabs = tabs.sort((a, b)=>{
				let ia = this.settings.TabOrder.findIndex((t)=>t == a.ID);
				let ib = this.settings.TabOrder.findIndex((t)=>t == b.ID);
				return ia - ib;
			});
			this.previousTabs = this.tabs.map(t=>t.getJSON());
		}
	},
	async mounted() {
		ToolbarService.title = this.$ct("title");
		await this.fetchTabs();
		try{
			this.cashierSettings = UserConfigService.getCashierSettings();
			this.connectedDisplay = this.cashierSettings.ConnectedDisplay;
		}catch(err){
		}
		this.loaded = true;
		this.loadItems();
	},
	components: {
		TinyAccountInfo,
		AppAccountReactivateEmail,
		AppGridContainer,
		AppDashboardItem,
		AppSettingsBar,
		AppCustomSettings,
		AppQuickAddButtons,
	},
	i18nEx: {
		componentPrefix: "views.HomeRoute"
	}
});
